import DataKeys from 'k8s/DataKeys'
import getDataSelector from 'core/utils/getDataSelector'
import { createSharedSelector, selectParamsFromProps } from 'core/utils/selectorHelpers'
import { imagesByIdSelector } from '../images/selectors'
import {
  basicUsersByIdSelector,
  federatedUsersByIdSelector,
} from 'account/components/userManagement/users/selectors'
import { emptyObj } from 'utils/fp'
import { AppSelector } from 'app/store'
import { IVirtualMachinesSelector, ServerLite } from './model'
import { serverGroupsByInstanceIdSelector } from './server-groups/selectors'
import {
  allTenantsSelector,
  userTenantsSelector,
} from 'app/plugins/account/components/userManagement/tenants/selectors'
import store from 'app/store'
import { migrationsSelector } from './migrations/selectors'
import { UNASSIGNED_CLUSTER } from '../infrastructure/selectors'
import { leasePoliciesByTenantIdSelector, leasesByVmIdSelector } from './leases/selectors'
import moment from 'moment'
import {
  cpuImagePath,
  memoryImagePath,
  storageImagePath,
} from '../orchestration/app-catalog/constant'
import { createSelector } from '@reduxjs/toolkit'
import { makeGetVMIdsByAppSelector } from '../orchestration/repositories/selector'
import { latestMigrationByVmSelector } from './migrations/selectors'
import { User } from 'api-client/keystone.model'
import { volumeSnapshotsSelector } from '../storage/snapshots/selectors'

const { dispatch } = store

export const VOLUME_BACKED_VM_STRING = 'None (Bootable Volume Used)'

// Can't import this from volume selectors due to circular dependency
const rawVolumesByIdSelector = createSharedSelector(
  getDataSelector<DataKeys.OpenstackVolumesLite>(DataKeys.OpenstackVolumesLite),
  (volumes) => {
    return volumes.reduce((accum, volume) => {
      return {
        ...accum,
        [volume.id]: { volume },
      }
    }, {})
  },
)

// const allRawVolumesByIdSelector = createSharedSelector(
//   getDataSelector<DataKeys.AllOpenstackVolumes>(DataKeys.AllOpenstackVolumes),
//   (volumes) => {
//     return volumes.reduce((accum, volume) => {
//       return {
//         ...accum,
//         [volume.id]: volume,
//       }
//     }, {})
//   },
// )

const snapshotsByVolumeIdSelector = createSharedSelector(
  volumeSnapshotsSelector,
  (volumeSnapshots) => {
    return volumeSnapshots.reduce((accum, snapshot) => {
      if (accum[snapshot?.volume_id]) {
        return {
          ...accum,
          [snapshot?.volume_id]: [...accum[snapshot.volume_id], snapshot],
        }
      }
      return {
        ...accum,
        [snapshot?.volume_id]: [snapshot],
      }
    }, {})
  },
)

const volumesByIdWithSnapshotsSelector = createSharedSelector(
  getDataSelector<DataKeys.AllOpenstackVolumes>(DataKeys.AllOpenstackVolumes),
  snapshotsByVolumeIdSelector,
  (volumes, volumeSnapshotsByVolumeId) => {
    return volumes.reduce((accum, volume) => {
      return {
        ...accum,
        [volume.id]: {
          ...volume,
          snapshots: volumeSnapshotsByVolumeId[volume?.id] || [],
        },
      }
    }, {})
  },
)
// End of volume selectors

export const virtualMachinesSelector: AppSelector<IVirtualMachinesSelector[]> = createSharedSelector(
  getDataSelector<DataKeys.OpenstackVirtualMachines>(DataKeys.OpenstackVirtualMachines),
  imagesByIdSelector,
  basicUsersByIdSelector,
  federatedUsersByIdSelector,
  volumesByIdWithSnapshotsSelector,
  serverGroupsByInstanceIdSelector,
  allTenantsSelector,
  userTenantsSelector,
  migrationsSelector,
  getDataSelector<DataKeys.ClusterHostGroups>(DataKeys.ClusterHostGroups),
  leasePoliciesByTenantIdSelector,
  leasesByVmIdSelector,
  latestMigrationByVmSelector,
  (
    vms,
    imagesById,
    usersById,
    federatedUsersById,
    volumesById,
    serverGroupsByInstanceId,
    allTenants,
    userTenants,
    migrations,
    clusterHostGroups,
    leasePoliciesByTenantId,
    leasesByVmId,
    latestMigrationByVmId,
  ) => {
    const { session } = store.getState()
    const currentUserId = session?.userDetails?.id
    const currentUserName = session?.userDetails?.displayName
    return vms.map((vm) => {
      const imageId = vm?.image?.id
      const user = ({ ...usersById, ...federatedUsersById } as Record<string, User>)[vm?.user_id]
      const volumeIds = vm?.['os-extended-volumes:volumes_attached']?.map((vol) => vol.id) || []
      const volumeDetails = volumeIds.reduce((accum, id) => {
        return {
          ...accum,
          [id]: volumesById[id],
        }
      }, {})
      const volumeNames = volumeIds.map((id) => {
        return volumesById[id]?.name || id
      })
      const migrationAttemptedFrom = migrations?.lastMigrationByVmId?.[vm.id]?.startHost
      const migrationDestination = migrations?.lastMigrationByVmId?.[vm.id]?.endHost

      // Check if the availability zone is a cluster
      // pinned_availability_zone is desired AZ and will always be
      // the same as actual assigned AZ. We want to show this too
      // to the user in case VM creation failed
      const clusterName = clusterHostGroups?.some((cluster) =>
        [vm?.['OS-EXT-AZ:availability_zone'], vm?.pinned_availability_zone].includes(cluster?.name),
      )
        ? vm?.pinned_availability_zone || vm?.['OS-EXT-AZ:availability_zone']
        : UNASSIGNED_CLUSTER

      // Leases
      const leasePolicy = leasePoliciesByTenantId[vm.tenant_id]
      const customLease = leasesByVmId[vm.id]
      const expirationTime = customLease
        ? customLease.expiry
        : leasePolicy
        ? moment(vm?.created)
            .add(leasePolicy.expiry_mins, 'minutes')
            .format('YYYY-MM-DDTHH:mm:ss')
        : null
      const leaseAction = customLease ? customLease.action : leasePolicy ? leasePolicy.action : null
      return {
        ...vm,
        flavorName: vm?.flavor?.original_name || 'Unknown',
        taskState: vm['OS-EXT-STS:task_state'],
        state: vm['OS-EXT-STS:vm_state'],
        pausedPowerState:
          vm['OS-EXT-STS:vm_state'] === 'active' && vm['OS-EXT-STS:power_state'] === 3,
        imageDetails: imagesById[imageId],
        imageName:
          imagesById[imageId]?.name ||
          imageId ||
          (volumeIds?.length ? VOLUME_BACKED_VM_STRING : 'Unknown'),
        ownerName: user?.displayname
          ? `${user?.email || vm?.user_id} (${user.displayname})`
          : user?.email || vm?.user_id,
        volumeDetails,
        volumeNames,
        volumeIds,
        serverGroup: serverGroupsByInstanceId?.[vm.id],
        tenantName: [...userTenants, ...allTenants]?.find((tenant) => tenant.id === vm?.tenant_id)
          ?.name,
        migrationAttemptedFrom,
        migrationDestination,
        securityGroupNames: vm?.security_groups?.map((group) => group?.name),
        clusterName,
        customLease,
        leasePolicy,
        leaseAction,
        expirationTime,
        migration: latestMigrationByVmId?.[vm?.id],
        migrationPriority: vm?.metadata?.['migration-priority'] || '',
      }
    })
  },
)

export const allVirtualMachinesSelector: AppSelector<IVirtualMachinesSelector[]> = createSharedSelector(
  getDataSelector<DataKeys.AllOpenstackVirtualMachines>(DataKeys.AllOpenstackVirtualMachines),
  imagesByIdSelector,
  basicUsersByIdSelector,
  volumesByIdWithSnapshotsSelector,
  allTenantsSelector,
  userTenantsSelector,
  migrationsSelector,
  getDataSelector<DataKeys.ClusterHostGroups>(DataKeys.ClusterHostGroups),
  latestMigrationByVmSelector,
  (
    vms,
    imagesById,
    usersById,
    volumesById,
    allTenants,
    userTenants,
    migrations,
    clusterHostGroups,
    latestMigrationByVmId,
  ) => {
    return vms.map((vm) => {
      const imageId = vm?.image?.id
      const user = usersById[vm?.user_id]
      const volumeIds = vm?.['os-extended-volumes:volumes_attached']?.map((vol) => vol.id) || []
      const volumeDetails = volumeIds.reduce((accum, id) => {
        return {
          ...accum,
          [id]: volumesById[id],
        }
      }, {})
      const volumeNames = volumeIds.map((id) => {
        return volumesById[id]?.name || id
      })
      const migrationAttemptedFrom = migrations?.lastMigrationByVmId?.[vm.id]?.startHost
      const migrationDestination = migrations?.lastMigrationByVmId?.[vm.id]?.endHost

      // Check if the availability zone is a cluster
      const clusterName = clusterHostGroups?.some((cluster) =>
        [vm?.['OS-EXT-AZ:availability_zone'], vm?.pinned_availability_zone].includes(cluster?.name),
      )
        ? vm?.pinned_availability_zone || vm?.['OS-EXT-AZ:availability_zone']
        : UNASSIGNED_CLUSTER

      return {
        ...vm,
        flavorName: vm?.flavor?.original_name || 'Unknown',
        taskState: vm['OS-EXT-STS:task_state'],
        state: vm['OS-EXT-STS:vm_state'],
        pausedPowerState:
          vm['OS-EXT-STS:vm_state'] === 'active' && vm['OS-EXT-STS:power_state'] === 3,
        imageDetails: imagesById[imageId],
        imageName:
          imagesById[imageId]?.name ||
          imageId ||
          (volumeIds?.length ? VOLUME_BACKED_VM_STRING : 'Unknown'),
        ownerName: user?.displayname
          ? `${user?.email || vm?.user_id} (${user.displayname})`
          : user?.email || vm?.user_id,
        volumeDetails,
        volumeNames,
        volumeIds,
        tenantName: [...userTenants, ...allTenants]?.find((tenant) => tenant.id === vm?.tenant_id)
          ?.name,
        migrationAttemptedFrom,
        migrationDestination,
        securityGroupNames: vm?.security_groups?.map((group) => group?.name),
        clusterName,
        migration: latestMigrationByVmId?.[vm?.id],
        migrationPriority: vm?.metadata?.['migration-priority'] || '',
      }
    })
  },
)

export const singleVirtualMachineSelector: AppSelector<IVirtualMachinesSelector[]> = createSharedSelector(
  getDataSelector<DataKeys.SingleOpenstackVirtualMachine>(DataKeys.SingleOpenstackVirtualMachine),
  imagesByIdSelector,
  basicUsersByIdSelector,
  federatedUsersByIdSelector,
  rawVolumesByIdSelector,
  serverGroupsByInstanceIdSelector,
  allTenantsSelector,
  userTenantsSelector,
  migrationsSelector,
  getDataSelector<DataKeys.ClusterHostGroups>(DataKeys.ClusterHostGroups),
  leasePoliciesByTenantIdSelector,
  leasesByVmIdSelector,
  latestMigrationByVmSelector,
  (
    vms,
    imagesById,
    usersById,
    federatedUsersById,
    volumesById,
    serverGroupsByInstanceId,
    allTenants,
    userTenants,
    migrations,
    clusterHostGroups,
    leasePoliciesByTenantId,
    leasesByVmId,
    latestMigrationByVmId,
  ) => {
    const { session } = store.getState()
    const currentUserId = session?.userDetails?.id
    const currentUserName = session?.userDetails?.displayName
    return vms.map((vm) => {
      const imageId = vm?.image?.id
      const user = ({ ...usersById, ...federatedUsersById } as Record<string, User>)[vm?.user_id]
      const volumeIds = vm?.['os-extended-volumes:volumes_attached']?.map((vol) => vol.id) || []
      const volumeDetails = volumeIds.reduce((accum, id) => {
        return {
          ...accum,
          [id]: volumesById[id],
        }
      }, {})
      const volumeNames = volumeIds.map((id) => {
        return volumesById[id]?.name || id
      })
      const migrationAttemptedFrom = migrations?.lastMigrationByVmId?.[vm.id]?.startHost
      const migrationDestination = migrations?.lastMigrationByVmId?.[vm.id]?.endHost

      // Check if the availability zone is a cluster
      // pinned_availability_zone is desired AZ and will always be
      // the same as actual assigned AZ. We want to show this too
      // to the user in case VM creation failed
      const clusterName = clusterHostGroups?.some((cluster) =>
        [vm?.['OS-EXT-AZ:availability_zone'], vm?.pinned_availability_zone].includes(cluster?.name),
      )
        ? vm?.pinned_availability_zone || vm?.['OS-EXT-AZ:availability_zone']
        : UNASSIGNED_CLUSTER

      // Leases
      const leasePolicy = leasePoliciesByTenantId[vm.tenant_id]
      const customLease = leasesByVmId[vm.id]
      const expirationTime = customLease
        ? customLease.expiry
        : leasePolicy
        ? moment(vm?.created)
            .add(leasePolicy.expiry_mins, 'minutes')
            .format('YYYY-MM-DDTHH:mm:ss')
        : null
      const leaseAction = customLease ? customLease.action : leasePolicy ? leasePolicy.action : null
      return {
        ...vm,
        flavorName: vm?.flavor?.original_name || 'Unknown',
        taskState: vm['OS-EXT-STS:task_state'],
        state: vm['OS-EXT-STS:vm_state'],
        pausedPowerState:
          vm['OS-EXT-STS:vm_state'] === 'active' && vm['OS-EXT-STS:power_state'] === 3,
        imageDetails: imagesById[imageId],
        imageName:
          imagesById[imageId]?.name ||
          imageId ||
          (volumeIds?.length ? 'None (Bootable Volume Used)' : 'Unknown'),
        ownerName: user?.displayname
          ? `${user?.email || vm?.user_id} (${user.displayname})`
          : user?.email || vm?.user_id,
        volumeDetails,
        volumeNames,
        volumeIds,
        serverGroup: serverGroupsByInstanceId?.[vm.id],
        tenantName: [...userTenants, ...allTenants]?.find((tenant) => tenant.id === vm?.tenant_id)
          ?.name,
        migrationAttemptedFrom,
        migrationDestination,
        securityGroupNames: vm?.security_groups?.map((group) => group?.name),
        clusterName,
        customLease,
        leasePolicy,
        leaseAction,
        expirationTime,
        migration: latestMigrationByVmId?.[vm?.id],
        migrationPriority: vm?.metadata?.['migration-priority'] || 'default',
      }
    })
  },
)

export const selfVirtualMachinesSelector: AppSelector<IVirtualMachinesSelector[]> = createSharedSelector(
  getDataSelector<DataKeys.SelfOpenstackVirtualMachines>(DataKeys.SelfOpenstackVirtualMachines),
  imagesByIdSelector,
  basicUsersByIdSelector,
  federatedUsersByIdSelector,
  rawVolumesByIdSelector,
  serverGroupsByInstanceIdSelector,
  allTenantsSelector,
  userTenantsSelector,
  migrationsSelector,
  getDataSelector<DataKeys.ClusterHostGroups>(DataKeys.ClusterHostGroups),
  leasePoliciesByTenantIdSelector,
  leasesByVmIdSelector,
  latestMigrationByVmSelector,
  (
    vms,
    imagesById,
    usersById,
    federatedUsersById,
    volumesById,
    serverGroupsByInstanceId,
    allTenants,
    userTenants,
    migrations,
    clusterHostGroups,
    leasePoliciesByTenantId,
    leasesByVmId,
    latestMigrationByVmId,
  ) => {
    const { session } = store.getState()
    const currentUserId = session?.userDetails?.id
    const currentUserName = session?.userDetails?.displayName
    return vms.map((vm) => {
      const imageId = vm?.image?.id
      const user = ({ ...usersById, ...federatedUsersById } as Record<string, User>)[vm?.user_id]
      const volumeIds = vm?.['os-extended-volumes:volumes_attached']?.map((vol) => vol.id) || []
      const volumeDetails = volumeIds.reduce((accum, id) => {
        return {
          ...accum,
          [id]: volumesById[id],
        }
      }, {})
      const volumeNames = volumeIds.map((id) => {
        return volumesById[id]?.name || id
      })
      const migrationAttemptedFrom = migrations?.lastMigrationByVmId?.[vm.id]?.startHost
      const migrationDestination = migrations?.lastMigrationByVmId?.[vm.id]?.endHost

      // Check if the availability zone is a cluster
      // pinned_availability_zone is desired AZ and will always be
      // the same as actual assigned AZ. We want to show this too
      // to the user in case VM creation failed
      const clusterName = clusterHostGroups?.some((cluster) =>
        [vm?.['OS-EXT-AZ:availability_zone'], vm?.pinned_availability_zone].includes(cluster?.name),
      )
        ? vm?.pinned_availability_zone || vm?.['OS-EXT-AZ:availability_zone']
        : UNASSIGNED_CLUSTER

      // Leases
      const leasePolicy = leasePoliciesByTenantId[vm.tenant_id]
      const customLease = leasesByVmId[vm.id]
      const expirationTime = customLease
        ? customLease.expiry
        : leasePolicy
        ? moment(vm?.created)
            .add(leasePolicy.expiry_mins, 'minutes')
            .format('YYYY-MM-DDTHH:mm:ss')
        : null
      const leaseAction = customLease ? customLease.action : leasePolicy ? leasePolicy.action : null
      return {
        ...vm,
        flavorName: vm?.flavor?.original_name || 'Unknown',
        taskState: vm['OS-EXT-STS:task_state'],
        state: vm['OS-EXT-STS:vm_state'],
        pausedPowerState:
          vm['OS-EXT-STS:vm_state'] === 'active' && vm['OS-EXT-STS:power_state'] === 3,
        imageDetails: imagesById[imageId],
        imageName:
          imagesById[imageId]?.name ||
          imageId ||
          (volumeIds?.length ? 'None (Bootable Volume Used)' : 'Unknown'),
        ownerName: user?.displayname
          ? `${user?.email || vm?.user_id} (${user.displayname})`
          : user?.email || vm?.user_id,
        volumeDetails,
        volumeNames,
        volumeIds,
        serverGroup: serverGroupsByInstanceId?.[vm.id],
        tenantName: [...userTenants, ...allTenants]?.find((tenant) => tenant.id === vm?.tenant_id)
          ?.name,
        migrationAttemptedFrom,
        migrationDestination,
        securityGroupNames: vm?.security_groups?.map((group) => group?.name),
        clusterName,
        customLease,
        leasePolicy,
        leaseAction,
        expirationTime,
        migration: latestMigrationByVmId?.[vm?.id],
        migrationPriority: vm?.metadata?.['migration-priority'] || 'default',
      }
    })
  },
)

// TODO: PERFORMANCE IMPACT - this can be replace with a simple volume selector
export const virtualMachinesByIdSelector = createSharedSelector(virtualMachinesSelector, (vms) =>
  vms.reduce((accum, vm) => {
    return {
      ...accum,
      [vm.id]: vm,
    }
  }, emptyObj),
)

export const virtualMachinesByHostSelector = createSharedSelector(virtualMachinesSelector, (vms) =>
  vms.reduce((accum, vm) => {
    const hostId = vm?.['OS-EXT-SRV-ATTR:host']
    return {
      ...accum,
      [hostId]: accum[hostId] ? [...accum[hostId], vm] : [vm],
    }
  }, emptyObj),
)

export const virtualMachinesByIpSelector = createSharedSelector(virtualMachinesSelector, (vms) =>
  vms.reduce((accum, vm) => {
    const vmAddresses =
      Object.entries(vm?.addresses)?.reduce((accum, [name, addresses]) => {
        const addressesIps = addresses?.reduce((accum, address) => {
          return {
            ...accum,
            [address?.addr]: vm,
          }
        }, {})
        return {
          ...accum,
          ...addressesIps,
        }
      }, {}) || {}
    return {
      ...accum,
      ...vmAddresses,
    }
  }, emptyObj),
)

export const computeQuotasSelector = createSharedSelector(virtualMachinesSelector, (vms) => {
  return vms?.reduce((accum, vm) => {
    return {
      ...accum,
      [vm?.tenant_id]: {
        ...accum[vm?.tenant_id],
        cores: (accum[vm?.tenant_id]?.cores || 0) + (vm?.flavor?.vcpus || 0),
        ram: (accum[vm?.tenant_id]?.ram || 0) + (vm?.flavor?.ram || 0),
      },
    }
  }, {})
})

export const makeVmCountsSelector = (
  defaultParams: { allTenants?: boolean } = { allTenants: false },
) => {
  const selectParams = selectParamsFromProps(defaultParams)
  return createSharedSelector(
    virtualMachinesSelector,
    allVirtualMachinesSelector,
    selectParams,
    (vms, allVms, params) => {
      const vmScope = params?.allTenants ? allVms : vms
      return vmScope?.reduce(
        (accum, vm) => {
          if (['active', 'building'].includes(vm?.state)) {
            accum.vmCounts.success += 1
          } else if (['paused', 'suspended', 'stopped'].includes(vm?.state)) {
            accum.vmCounts.warning += 1
          } else {
            accum.vmCounts.error += 1
          }

          //it should first check the metadata values for hotplug as it contains the real time info whenever the VM gets hotplugged
          const hotplugMemory = Number(vm?.metadata?.['HOTPLUG_MEMORY'])
          const hotplugVcpus = Number(vm?.metadata?.['HOTPLUG_CPU'])

          accum.computeQuotas[vm?.tenant_id] = {
            ...accum.computeQuotas[vm?.tenant_id],
            cores:
              (accum.computeQuotas[vm?.tenant_id]?.cores || 0) +
              (hotplugVcpus || vm?.flavor?.vcpus || 0),
            ram:
              (accum.computeQuotas[vm?.tenant_id]?.ram || 0) +
              (hotplugMemory || vm?.flavor?.ram || 0),
          }

          return accum
        },
        {
          vmCounts: { success: 0, warning: 0, error: 0 },
          computeQuotas: {},
        },
      )
    },
  )
}

export const makeGetvirtualMachinesByAppSelector = (
  defaultParams: { appId?: string } = {
    appId: '',
  },
) => {
  const selectParams = selectParamsFromProps(defaultParams)
  const vmUidsByAppSelector = makeGetVMIdsByAppSelector()
  return createSelector(virtualMachinesSelector, selectParams, (vms, params) => {
    const vmUids = vmUidsByAppSelector(store.getState(), { appId: params?.appId || '' })
    // Filter VMs based on the retrieved VM IDs
    const filteredVms = vms?.filter((vm) => vmUids.includes(vm?.id))

    // Return the filtered VMs in the expected format
    return filteredVms?.map((vm) => ({
      name: vm?.name || `VM ${vm?.id}`,
      id: vm?.id,
      status: vm?.state || 'Unknown',
      taskState: vm?.taskState || 'Unknown',
      addresses: Object.entries(vm?.addresses || {}).reduce((accum, [networkName, addresses]) => {
        const addressList = addresses.map((address) => address.addr).filter(Boolean)
        return addressList.length ? { ...accum, [networkName]: addressList } : accum
      }, {}),
      flavor: [
        {
          label: 'CPUs',
          value: vm?.flavor?.vcpus || '-',
          imagePath: cpuImagePath,
          showDivider: true,
        },
        {
          label: 'Storage',
          value: vm?.flavor?.disk || '-',
          imagePath: storageImagePath,
          showDivider: true,
        },
        {
          label: 'Memory',
          value: vm?.flavor?.ram || '-',
          imagePath: memoryImagePath,
          showDivider: false,
        },
      ],
    }))
  })
}

// Lite VM List Selectors
export const virtualMachinesLiteSelector = createSharedSelector(
  getDataSelector<DataKeys.OpenstackVirtualMachinesLite>(DataKeys.OpenstackVirtualMachinesLite),
  (vms) => vms,
)

export const virtualMachinesLiteByIdSelector = createSharedSelector(
  virtualMachinesLiteSelector,
  (vms) =>
    vms.reduce((accum, vm) => {
      return {
        ...accum,
        [vm.id]: vm,
      }
    }, emptyObj),
)

export const allVirtualMachinesLiteSelector = createSharedSelector(
  getDataSelector<DataKeys.AllOpenstackVirtualMachinesLite>(
    DataKeys.AllOpenstackVirtualMachinesLite,
  ),
  (vms) => vms,
)

export const allVirtualMachinesLiteByIdSelector = createSharedSelector(
  allVirtualMachinesLiteSelector,
  (vms) =>
    vms.reduce((accum, vm) => {
      return {
        ...accum,
        [vm.id]: vm,
      }
    }, emptyObj),
)

export const selfVirtualMachinesLiteSelector = createSharedSelector(
  getDataSelector<DataKeys.SelfOpenstackVirtualMachinesLite>(
    DataKeys.SelfOpenstackVirtualMachinesLite,
  ),
  (vms) => vms,
)

export const selfStatewiseVirtualMachinesLiteSelector = createSharedSelector(
  getDataSelector<DataKeys.SelfOpenstackStatewiseVirtualMachinesLite>(
    DataKeys.SelfOpenstackStatewiseVirtualMachinesLite,
  ),
  getDataSelector<DataKeys.SelfOpenstackVirtualMachinesLite>(
    DataKeys.SelfOpenstackVirtualMachinesLite,
  ),
  (statewiseVms, totalVMs) => {
    const success =
      statewiseVms?.find((vm) => vm?.state === 'active')?.servers?.length +
        statewiseVms?.find((vm) => vm?.state === 'building')?.servers?.length || 0

    const warning =
      statewiseVms?.find((vm) => vm?.state === 'stopped')?.servers?.length +
        statewiseVms?.find((vm) => vm?.state === 'paused')?.servers?.length || 0

    const error =
      statewiseVms?.length > 0 && totalVMs?.length > 0
        ? totalVMs?.length - success - warning || 0
        : 0

    return { success, warning, error }
  },
)

export const statewiseVirtualMachinesLiteSelector = createSharedSelector(
  getDataSelector<DataKeys.OpenstackStatewiseVirtualMachinesLite>(
    DataKeys.OpenstackStatewiseVirtualMachinesLite,
  ),
  getDataSelector<DataKeys.OpenstackVirtualMachinesLite>(DataKeys.OpenstackVirtualMachinesLite),
  (statewiseVms, totalVMs) => {
    const success =
      statewiseVms?.find((vm) => vm?.state === 'active')?.servers?.length +
        statewiseVms?.find((vm) => vm?.state === 'building')?.servers?.length || 0

    const warning =
      statewiseVms?.find((vm) => vm?.state === 'stopped')?.servers?.length +
        statewiseVms?.find((vm) => vm?.state === 'suspended')?.servers?.length +
        statewiseVms?.find((vm) => vm?.state === 'paused')?.servers?.length || 0

    const error =
      statewiseVms?.length > 0 && totalVMs?.length > 0
        ? totalVMs?.length - success - warning || 0
        : 0

    return {
      success,
      warning,
      error,
    }
  },
)

export const allStatewiseVirtualMachinesLiteSelector = createSharedSelector(
  getDataSelector<DataKeys.AllOpenstackStatewiseVirtualMachinesLite>(
    DataKeys.AllOpenstackStatewiseVirtualMachinesLite,
  ),
  getDataSelector<DataKeys.AllOpenstackVirtualMachinesLite>(
    DataKeys.AllOpenstackVirtualMachinesLite,
  ),
  (statewiseVms, totalVMs) => {
    const success =
      statewiseVms?.find((vm) => vm?.state === 'active')?.servers?.length +
        statewiseVms?.find((vm) => vm?.state === 'building')?.servers?.length || 0

    const warning =
      statewiseVms?.find((vm) => vm?.state === 'stopped')?.servers?.length +
        statewiseVms?.find((vm) => vm?.state === 'suspended')?.servers?.length +
        statewiseVms?.find((vm) => vm?.state === 'paused')?.servers?.length || 0

    const error =
      statewiseVms?.length > 0 && totalVMs?.length > 0
        ? totalVMs?.length - success - warning || 0
        : 0

    return {
      success,
      warning,
      error,
    }
  },
)

// Selector for Attach Volume - Send only active, stopped and suspended VMs
export const vmListSelectorForAttachVolume = createSharedSelector(
  getDataSelector<DataKeys.OpenstackStatewiseVirtualMachinesLite>(
    DataKeys.OpenstackStatewiseVirtualMachinesLite,
  ),
  (statewiseVms) => {
    const activeVMs =
      statewiseVms
        ?.find((vm) => vm?.state === 'active')
        ?.servers?.map((vm) => ({
          ...vm,
          state: 'active',
        })) || []
    const stoppedVMs =
      statewiseVms
        ?.find((vm) => vm?.state === 'stopped')
        ?.servers?.map((vm) => ({
          ...vm,
          state: 'stopped',
        })) || []
    const suspendedVMs =
      statewiseVms
        ?.find((vm) => vm?.state === 'suspended')
        ?.servers?.map((vm) => ({
          ...vm,
          state: 'suspended',
        })) || []

    return [...activeVMs, ...stoppedVMs, ...suspendedVMs] as ServerLite[]
  },
)
